import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {Document} from '@contentful/rich-text-types'
import Image from 'next/image'
import {FC} from 'react'

import {ContentsList} from '@/components/contentful/ContentsList'
import {BLOG_CONTENT_WIDTH} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Author} from '@/utils/contentful'
import {withSequentialHeadingLevels} from '@/utils/contentful/headingLevels'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {BlogAuthor} from './BlogAuthor'
import {options} from './styledBlogOptions'

type BlogContentProps = {
  text: Document
  authors: Author[]
  hasListOfContents?: boolean
  image?: {src: string; alt: string}
}

export const BlogContent: FC<React.PropsWithChildren<BlogContentProps>> = ({
  text,
  authors,
  hasListOfContents,
  image,
}) => {
  return (
    <>
      <div className="blog-content">
        {image && (
          <div className="image">
            <Image
              src={image.src}
              alt={image.alt}
              style={{objectFit: 'cover'}}
              fill
              sizes={`${MOBILE_SCREENSIZE} 100vw, ${BLOG_CONTENT_WIDTH}`}
              priority
            />
          </div>
        )}
        {hasListOfContents && <ContentsList content={text.content} />}
        <article>{documentToReactComponents(withSequentialHeadingLevels(text), options)}</article>
        <div className="authors">
          {authors.map(({name, image, role, bio1, bio2}) => (
            <div key={name} className="author">
              <BlogAuthor name={name} imageSrc={image.url} role={role} bio1={bio1} bio2={bio2} />
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .blog-content {
          max-width: ${BLOG_CONTENT_WIDTH};
          margin: 0 auto;
        }
        .image {
          position: relative;
          width: 100%;
          aspect-ratio: 16 / 9;
          background-color: ${theme.palette.skeletonLoading.main};
          margin-top: -${theme.spacing[6]};
          margin-bottom: ${theme.spacing[8]};
        }
        .authors {
          margin-top: ${theme.spacing[6]};
          padding-top: ${theme.spacing[6]};
          border-top: 2px solid ${theme.palette.butter[200]};
        }
        .author + .author {
          margin-top: ${theme.spacing[6]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .image {
            margin-top: -${theme.spacing[4]};
            margin-bottom: ${theme.spacing[6]};
          }
          .author + .author {
            margin-top: ${theme.spacing[5]};
          }
        }
      `}</style>
      <style jsx global>{`
        .blog-content p {
          ${theme.typography.text20.desktop};
          color: ${theme.palette.text.default};
          margin-top: ${theme.spacing[5]};
          margin-bottom: 0;
        }
        .blog-content *:first-child {
          margin-top: 0;
        }
        .blog-content a {
          color: ${theme.palette.text.primary};
          font-weight: ${theme.fontWeight.semibold};
          text-decoration: underline;
        }
        .blog-content a:hover {
          text-decoration: none;
        }
        @media ${MOBILE_SCREENSIZE} {
          .blog-content p {
            ${theme.typography.text20.mobile};
            margin-top: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
